export enum CellType {
  EMPTY = 0,
  WALL = 1,
  FOOD = 2,
  NEST = 3,
}

export enum SenseTarget {
  EMPTY = 0,
  WALL = 1,
  FOOD = 2,
  NEST = 3,
  ANT = 4,
}

export enum Opcode {
  SENSE,
  SMELL,
  SNIFF,
  PROBE,
  CARRYING,
  SET,
  ADD,
  SUB,
  MOD,
  MUL,
  DIV,
  AND,
  OR,
  XOR,
  LSHIFT,
  RSHIFT,
  RANDOM,
  MARK,
  JMP,
  CALL,
  JEQ,
  JNE,
  JGT,
  JLT,
  MOVE,
  PICKUP,
  DROP,
  ID,
  NOP,
  TAG,
}

export type Operand =
  | { type: 'lit'; val: number }
  | { type: 'reg'; val: number };

export interface Instruction {
  op: Opcode;
  operands: Operand[];
}

export interface Program {
  instructions: Instruction[];
  sourceLines?: number[];
  tagNames?: Map<number, string>;
  registerAliases?: Map<number, string>;
}

export const NUM_REGISTERS = 8;

export interface Ant {
  x: number;
  y: number;
  carrying: boolean;
  regs: number[];
  pc: number;
  tag: number;
}

export interface SwarmMap {
  width: number;
  height: number;
  cells: Uint8Array;
  food: Uint16Array;
  pheromones: Uint16Array;
  visitCounts: Uint32Array;
  nestX: number;
  nestY: number;
  totalFood: number;
  seed: number;
  name: string;
}

export const NUM_TAG_CHANNELS = 8;

export interface World {
  map: SwarmMap;
  ants: Ant[];
  tick: number;
  foodCollected: number;
  program: Program;
  bytecode: Int32Array;
  antGrid: Uint8Array;
  rngState: number;
  // Sparse pheromone decay: track only cells with non-zero pheromones
  pheroActive: Uint8Array;  // 1 if cell has any active pheromone
  pheroList: Uint32Array;   // compact list of active cell indices
  pheroListLen: number;
}

// Bytecode layout: 5 slots per instruction [opcode, flags, arg0, arg1, arg2]
// flags bitmask: bit0 = arg0 is reg, bit1 = arg1 is reg, bit2 = arg2 is reg
export const BC_STRIDE = 5;

export interface SimConfig {
  mapWidth: number;
  mapHeight: number;
  antCount: number;
  maxTicks: number;
  pheromoneDecay: number;
  maxOpsPerTick: number;
  senseRange: number;
}

export interface SimResult {
  foodCollected: number;
  totalFood: number;
  ticks: number;
  mapSeed: number;
  mapName: string;
}

export const EVAL_MAP_COUNT = 120;

export const DEFAULT_CONFIG: SimConfig = {
  mapWidth: 128,
  mapHeight: 128,
  antCount: 200,
  maxTicks: 2000,
  pheromoneDecay: 0.97,
  maxOpsPerTick: 64,
  senseRange: 1,
};

export const NUM_PHEROMONE_CHANNELS = 4;

export const DIR_HERE = 5;
export const DIR_RANDOM = 6;

// Direction encoding: NONE=0, N=1, E=2, S=3, W=4, HERE=5
//                      none   N   E  S   W  here
export const DIR_DX = [  0,    0,  1, 0, -1,  0  ] as const;
export const DIR_DY = [  0,   -1,  0, 1,  0,  0  ] as const;
